import { debounce } from 'lodash';
import { useMemo, useState, ReactNode, useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import CircularProgress from '@mui/material/CircularProgress';

import { useResponsive } from 'src/hooks/use-responsive';
import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll';

import { SortConfig } from 'src/services/api.types';
import { SortOrder } from 'src/services/common.types';

import Scrollbar from 'src/components/scrollbar';
import EmptyContent from 'src/components/empty-content';
import { TableNoData, TableSkeleton } from 'src/components/table';

import { ColumnDef, ColumnFilterDef } from './data-list.types';
import { encodeColumnFilterValues, decodeColumnFilterValues } from './column-filter-value';

// ----------------------------------------------------------------------

interface DataListProps<TRow> {
  /** Already resolved to the visible, ordered set (see resolveColumns). */
  columns: ColumnDef<TRow>[];
  rows: TRow[];
  getRowId: (row: TRow) => string;
  /** True while any query (initial, next page, or re-filter) is in flight. */
  isSearching: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  sort: SortConfig;
  onSortChange: (sort: SortConfig) => void;
  columnSearch: Record<string, string>;
  onColumnSearchChange: (key: string, value: string) => void;
  /** Caller-owned toolbar (global search, create/import buttons, the Columns picker trigger). */
  toolbar?: ReactNode;
  selectable?: boolean;
  selectedIds?: string[];
  onSelectRow?: (id: string) => void;
  onSelectAll?: (checked: boolean, ids: string[]) => void;
  /** Leading per-row cell before the configurable columns (e.g. the record-panel eye, JT1-1529). */
  renderRowLeading?: (row: TRow) => ReactNode;
  /** Trailing per-row actions cell (kebab menu etc.) — owned by the consumer. */
  renderRowActions?: (row: TRow) => ReactNode;
  emptyLabel?: string;
  /**
   * Rendered in place of the empty state when the REQUEST failed rather than matched nobody
   * (JT1-1647). A failed search and a search that found nothing are different answers, and the list
   * has no way to tell them apart from `rows` alone — so the caller says which one this is.
   */
  errorContent?: ReactNode;
  searchPlaceholder?: string;
}

// ----------------------------------------------------------------------

/** Per-column search input with local state and a ~300ms debounce so typing doesn't fire a request per keystroke. */
/**
 * A column's header filter, or null when it has none. `searchable: true` is the long-standing
 * shorthand for a free-text filter; `filter` wins where both are set.
 */
function resolveColumnFilter<TRow>(col: ColumnDef<TRow>): ColumnFilterDef | null {
  if (col.filter) return col.filter;
  return col.searchable ? { kind: 'text' } : null;
}

/**
 * Picks the control for a column's filter kind. Non-filterable columns still render an empty cell
 * so the filter row stays aligned with the header above it.
 *
 * Only the text control is debounced. Choosing from a dropdown is a single deliberate act, not a
 * stream of keystrokes, so debouncing it would just delay the result — which is also why the
 * dropdowns are fully controlled and the text input is not.
 */
function ColumnFilterControl({
  filter,
  value,
  placeholder,
  onChange,
}: {
  filter: ColumnFilterDef | null;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  if (!filter) return null;

  if (filter.kind === 'select') {
    return <ColumnSelectFilter filter={filter} value={value} onChange={onChange} />;
  }

  if (filter.kind === 'boolean') {
    return <ColumnBooleanFilter filter={filter} value={value} onChange={onChange} />;
  }

  return <ColumnSearchInput initialValue={value} placeholder={placeholder ?? ''} onChange={onChange} />;
}

/** Multi-select filter over a closed set of options (a dropdown custom field, say). */
function ColumnSelectFilter({
  filter,
  value,
  onChange,
}: {
  filter: Extract<ColumnFilterDef, { kind: 'select' }>;
  value: string;
  onChange: (value: string) => void;
}) {
  const selected = useMemo(() => decodeColumnFilterValues(value), [value]);
  const labelByValue = useMemo(
    () => new Map(filter.options.map((option) => [option.value, option.label])),
    [filter.options]
  );

  return (
    <Select
      multiple
      displayEmpty
      size="small"
      fullWidth
      value={selected}
      onChange={(event) => {
        const next = event.target.value as unknown as string[];
        onChange(next.length ? encodeColumnFilterValues(next) : '');
      }}
      renderValue={(chosen) => {
        const values = chosen as string[];
        if (!values.length) return <Placeholder />;
        // One selection reads better as the thing itself; past that a count stays inside the cell.
        if (values.length === 1) return labelByValue.get(values[0]) ?? values[0];
        return `${values.length} selected`;
      }}
      sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: 13 } }}
      inputProps={{ 'aria-label': filter.options.length ? 'Filter' : 'Filter (no options)' }}
    >
      {filter.options.map((option) => (
        <MenuItem key={option.value} value={option.value} sx={{ py: 0.5 }}>
          <Checkbox size="small" checked={selected.includes(option.value)} sx={{ p: 0.5, mr: 0.5 }} />
          <Typography variant="body2" noWrap>{option.label}</Typography>
        </MenuItem>
      ))}
    </Select>
  );
}

/**
 * Three-state yes/no filter. "Any" is the empty value, which the list containers already treat as
 * "remove this key from column_filters" — so not filtering and filtering to nothing stay distinct.
 */
function ColumnBooleanFilter({
  filter,
  value,
  onChange,
}: {
  filter: Extract<ColumnFilterDef, { kind: 'boolean' }>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Select
      displayEmpty
      size="small"
      fullWidth
      value={value === 'true' || value === 'false' ? value : ''}
      onChange={(event) => onChange(event.target.value as string)}
      renderValue={(chosen) => {
        if (chosen === 'true') return filter.trueLabel ?? 'Yes';
        if (chosen === 'false') return filter.falseLabel ?? 'No';
        return <Placeholder />;
      }}
      sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: 13 } }}
      inputProps={{ 'aria-label': 'Filter' }}
    >
      <MenuItem value=""><Typography variant="body2" color="text.secondary">{filter.anyLabel ?? 'Any'}</Typography></MenuItem>
      <MenuItem value="true"><Typography variant="body2">{filter.trueLabel ?? 'Yes'}</Typography></MenuItem>
      <MenuItem value="false"><Typography variant="body2">{filter.falseLabel ?? 'No'}</Typography></MenuItem>
    </Select>
  );
}

/** Keeps an unset dropdown visually consistent with the text input's placeholder. */
function Placeholder() {
  return <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13 }}>—</Typography>;
}

function ColumnSearchInput({
  initialValue,
  placeholder,
  onChange,
}: {
  initialValue: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const [value, setValue] = useState(initialValue);

  const debounced = useMemo(() => debounce((v: string) => onChange(v), 300), [onChange]);
  useEffect(() => () => debounced.cancel(), [debounced]);

  // Keep in sync if the value is reset externally (e.g. Clear filters).
  useEffect(() => setValue(initialValue), [initialValue]);

  return (
    <TextField
      size="small"
      fullWidth
      value={value}
      placeholder={placeholder}
      onChange={(e) => {
        setValue(e.target.value);
        debounced(e.target.value);
      }}
      inputProps={{ 'aria-label': placeholder }}
      sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: 13 } }}
    />
  );
}

// ----------------------------------------------------------------------

/**
 * Registry-driven list surface shared by the candidates/contacts/companies lists (JT1-1528). Owns
 * the list MECHANICS — visible/ordered columns, per-column header sort (server), a per-column search
 * row, row selection, infinite scroll, and the searching/empty states. Domain concerns (row actions,
 * the toolbar, the column picker) are passed in as slots so the intricate per-entity wiring stays
 * with the consumer and this component never has to know about it.
 *
 * A custom header (not TableHeadCustom) is used deliberately: TableHeadCustom makes every column
 * sortable or none, but item 4 needs per-column sortability.
 */
export default function DataList<TRow>({
  columns,
  rows,
  getRowId,
  isSearching,
  hasMore,
  onLoadMore,
  sort,
  onSortChange,
  columnSearch,
  onColumnSearchChange,
  toolbar,
  selectable = false,
  selectedIds = [],
  onSelectRow,
  onSelectAll,
  renderRowLeading,
  renderRowActions,
  emptyLabel,
  errorContent,
  searchPlaceholder = 'Search',
}: DataListProps<TRow>) {
  const sentinelRef = useInfiniteScroll<HTMLTableRowElement>({ hasMore, isLoading: isSearching, onLoadMore });

  const isMobile = useResponsive('down', 'sm');

  // Separate sentinel for the card branch: the hook's ref is typed to the element it attaches to, and
  // only one branch renders at a time. The unused hook creates no observer — its effect bails when the
  // callback ref never fires.
  const cardSentinelRef = useInfiniteScroll<HTMLDivElement>({ hasMore, isLoading: isSearching, onLoadMore });

  const rowIds = rows.map(getRowId);
  const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const handleSort = (key: string) => {
    const isActive = sort.sort_by === key;
    const nextOrder = isActive && sort.sort_order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
    onSortChange({ sort_by: key, sort_order: nextOrder });
  };

  const colSpan = columns.length + (selectable ? 1 : 0) + (renderRowLeading ? 1 : 0) + (renderRowActions ? 1 : 0);
  const hasColumnSearch = columns.some((col) => !!resolveColumnFilter(col));
  const notFound = !isSearching && rows.length === 0;

  /**
   * Cards can't lay columns out left-to-right, so the registry's cardRole decides what leads. A
   * registry that annotates nothing still renders sensibly: the fixed column heads the card and
   * everything else becomes a labelled line.
   */
  const cardLayout = useMemo(() => {
    const visible = columns.filter((col) => col.cardRole !== 'hidden');
    const primary =
      visible.find((col) => col.cardRole === 'primary') ?? visible.find((col) => col.fixed) ?? visible[0];

    return {
      primary,
      secondary: visible.filter((col) => col.cardRole === 'secondary'),
      meta: visible.filter((col) => col.cardRole === 'meta'),
      // `col !== primary` matters when primary was picked by the fixed fallback: it has no cardRole,
      // so without the guard it would render again as a body line.
      body: visible.filter((col) => col !== primary && !col.cardRole),
    };
  }, [columns]);

  if (isMobile) {
    // Header sort and per-column search have nowhere to live without a header row — the same trade
    // the legacy mobile cards made. The caller's toolbar keeps global search and bulk actions.
    return (
      <Box>
        {toolbar}

        {isSearching && rows.length === 0 ? (
          <TableSkeleton />
        ) : (
          <Stack gap={1.5} sx={{ px: 2, pb: 2 }}>
            {rows.map((row) => {
              const id = getRowId(row);
              const isSelected = selectedIds.includes(id);

              return (
                <Card key={id} variant="outlined" sx={isSelected ? { borderColor: 'primary.main' } : undefined}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="flex-start" gap={1}>
                      {selectable && (
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onClick={() => onSelectRow?.(id)}
                          sx={{ mt: -0.5, ml: -1 }}
                        />
                      )}

                      {renderRowLeading && <Box>{renderRowLeading(row)}</Box>}

                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        {cardLayout.primary && <Box sx={{ typography: 'subtitle2' }}>{cardLayout.primary.render(row)}</Box>}
                        {cardLayout.secondary.map((col) => (
                          <Box key={col.key} sx={{ mt: 0.25 }}>{col.render(row)}</Box>
                        ))}
                      </Box>

                      {renderRowActions && <Box sx={{ ml: 'auto' }}>{renderRowActions(row)}</Box>}
                    </Stack>

                    {cardLayout.body.length > 0 && (
                      <Stack gap={0.75} sx={{ mt: 1.5 }}>
                        {cardLayout.body.map((col) => (
                          <Stack key={col.key} direction="row" alignItems="baseline" gap={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0 }}>
                              {col.label}
                            </Typography>
                            <Box sx={{ minWidth: 0 }}>{col.render(row)}</Box>
                          </Stack>
                        ))}
                      </Stack>
                    )}

                    {cardLayout.meta.length > 0 && (
                      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mt: 1.5 }}>
                        {cardLayout.meta.map((col) => (
                          <Box key={col.key}>{col.render(row)}</Box>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {hasMore && (
              <Box ref={cardSentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {errorContent ?? (notFound ? <EmptyContent filled title={emptyLabel || 'No Data'} sx={{ py: 6 }} /> : null)}
          </Stack>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {toolbar}

      {isSearching && rows.length === 0 ? (
        <TableSkeleton />
      ) : (
        <TableContainer sx={{ overflow: 'unset' }}>
          <Scrollbar>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someSelected}
                        checked={allSelected}
                        onChange={(e) => onSelectAll?.(e.target.checked, rowIds)}
                      />
                    </TableCell>
                  )}

                  {renderRowLeading && <TableCell padding="checkbox" />}

                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || 'left'}
                      sortDirection={sort.sort_by === col.key ? sort.sort_order : false}
                      sx={{ width: col.width, minWidth: col.minWidth }}
                    >
                      {col.sortable ? (
                        <TableSortLabel
                          active={sort.sort_by === col.key}
                          direction={sort.sort_by === col.key ? sort.sort_order : SortOrder.ASC}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}

                  {renderRowActions && <TableCell align="right" />}
                </TableRow>

                {hasColumnSearch && (
                  <TableRow>
                    {selectable && <TableCell padding="checkbox" />}
                    {renderRowLeading && <TableCell padding="checkbox" />}
                    {columns.map((col) => (
                      <TableCell key={col.key} sx={{ py: 0.5 }}>
                        <ColumnFilterControl
                          filter={resolveColumnFilter(col)}
                          value={columnSearch[col.key] ?? ''}
                          placeholder={searchPlaceholder}
                          onChange={(value) => onColumnSearchChange(col.key, value)}
                        />
                      </TableCell>
                    ))}
                    {renderRowActions && <TableCell />}
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {rows.map((row) => {
                  const id = getRowId(row);
                  return (
                    <TableRow key={id} hover selected={selectedIds.includes(id)}>
                      {selectable && (
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedIds.includes(id)} onClick={() => onSelectRow?.(id)} />
                        </TableCell>
                      )}
                      {renderRowLeading && <TableCell padding="checkbox">{renderRowLeading(row)}</TableCell>}
                      {columns.map((col) => (
                        <TableCell key={col.key} align={col.align || 'left'}>
                          {col.render(row)}
                        </TableCell>
                      ))}
                      {renderRowActions && <TableCell align="right">{renderRowActions(row)}</TableCell>}
                    </TableRow>
                  );
                })}

                {/* Infinite-scroll sentinel: a zero-height row observed to trigger the next page. */}
                {hasMore && (
                  <TableRow ref={sentinelRef}>
                    <TableCell colSpan={colSpan} align="center" sx={{ py: 2, border: 0 }}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}

                {errorContent ? (
                  <TableRow>
                    <TableCell colSpan={colSpan}>{errorContent}</TableCell>
                  </TableRow>
                ) : (
                  <TableNoData notFound={notFound} title={emptyLabel} sx={{ py: 6 }} />
                )}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>
      )}
    </Box>
  );
}
